/**
 * taxonomy.ts
 * ---------------------------------------------------------------------------
 * COPIED from espy-dashboard src/container/searcher-v2/config/taxonomy.ts so
 * the admin's workflow preview (WorkflowPreviewDialog) shows a workflow with
 * the same description, "You will get" list, runtime, API-only lock and
 * per-source cards the user's Search page gives it. The two apps share no
 * code — when the dashboard's file changes, copy it again.
 *
 * Client-side enrichment layer for the lookup catalogue.
 *
 * The backend already returns everything we strictly need on GET /lookup:
 *   { id, type, title, price, color, input, sources[] }
 *
 * The new Search page additionally needs, per lookup:
 *   - which INPUT KIND the user must hold (phone / email / name / social / …)
 *   - which CATEGORY shelf it belongs to
 *   - a one-line description
 *   - the list of fields the workflow returns ("You will get")
 *
 * Until those three land on the API (see BACKEND_CONTRACT.md §2), this file
 * supplies them. Everything degrades gracefully: an unknown `type` still
 * renders, it just lands in the "Other" category with a generic description.
 *
 * IMPORTANT: nothing here is a hard-coded list of workflows. Adding a workflow
 * in admin makes it appear automatically. This file only *decorates* it.
 */

// The dashboard's own Source / Type unions — plain strings here: the admin
// edits workflows of any type, including ones the dashboard has not met yet.
type Source = string
type Type = string

/* ------------------------------------------------------------------ */
/* Input kinds — the stable axis of step 1. Independent of workflow count. */
/* ------------------------------------------------------------------ */

export type InputKind =
  | 'phone'
  | 'email'
  | 'name'
  | 'social'
  | 'image'
  | 'ip'
  | 'password'
  | 'text'
  | 'bulk'
  | 'longtext'

export type InputKindMeta = {
  kind: InputKind
  label: string
  /** i18n key, falls back to `label` */
  i18nKey: string
  icon: string
  example?: string
  /** true = file / textarea upload instead of a single-line field */
  upload?: boolean
}

export const INPUT_KINDS: InputKindMeta[] = [
  { kind: 'phone', label: 'Phone number', i18nKey: 'searcher_v2.input.phone', icon: 'phone', example: '+972 54 211 1742' },
  { kind: 'email', label: 'E-mail', i18nKey: 'searcher_v2.input.email', icon: 'mail', example: 'name@domain.com' },
  { kind: 'name', label: 'Full name', i18nKey: 'searcher_v2.input.name', icon: 'user', example: 'John Smith' },
  { kind: 'social', label: 'Social ID', i18nKey: 'searcher_v2.input.social', icon: 'social', example: 'id264918381 or @handle' },
  { kind: 'image', label: 'Face / photo', i18nKey: 'searcher_v2.input.image', icon: 'image', upload: true },
  { kind: 'ip', label: 'IP address', i18nKey: 'searcher_v2.input.ip', icon: 'globe', example: '138.162.214.215' },
  { kind: 'password', label: 'Password', i18nKey: 'searcher_v2.input.password', icon: 'key', example: 'Paste a password or hash' },
  { kind: 'text', label: 'Text / domain', i18nKey: 'searcher_v2.input.text', icon: 'text', example: 'example.com' },
  { kind: 'bulk', label: 'Bulk CSV', i18nKey: 'searcher_v2.input.bulk', icon: 'list', upload: true },
  // sentiment_analysys's own kind — a whole passage (post/comment) to run
  // sentiment on, not a single-line identifier like plain 'text'. Old flow's
  // PopupTextUpload.tsx: a 6-row textarea + optional .txt attachment
  // (field.hint.text = "Enter text here"), backed by Joi.string().max(3500).
  // `upload: true` routes it through SearchValueField's dropzone-first
  // rendering, same family as 'image'/'bulk'.
  { kind: 'longtext', label: 'Text', i18nKey: 'searcher_v2.input.longtext', icon: 'text', upload: true },
]

export const inputKindMeta = (k: InputKind): InputKindMeta =>
  INPUT_KINDS.find((i) => i.kind === k) || INPUT_KINDS[INPUT_KINDS.length - 1]

/* ------------------------------------------------------------------ */
/* Categories — the shelves in the workflow library                    */
/* ------------------------------------------------------------------ */

export type CategoryId = 'phone' | 'email' | 'name' | 'digital' | 'ai' | 'other'

export type CategoryMeta = {
  id: CategoryId
  label: string
  i18nKey: string
  icon: string
  /** two stops, used for the tile gradient */
  gradient: [string, string]
}

export const CATEGORIES: CategoryMeta[] = [
  { id: 'phone', label: 'Phone intelligence', i18nKey: 'searcher_v2.cat.phone', icon: 'phone', gradient: ['#2D89F4', '#5FB0FF'] },
  { id: 'email', label: 'E-mail & leaks', i18nKey: 'searcher_v2.cat.email', icon: 'mail', gradient: ['#7C5CFF', '#A78BFA'] },
  { id: 'name', label: 'Name & face', i18nKey: 'searcher_v2.cat.name', icon: 'user', gradient: ['#0EA5A5', '#34D399'] },
  { id: 'digital', label: 'Digital footprint', i18nKey: 'searcher_v2.cat.digital', icon: 'social', gradient: ['#F59E0B', '#FBBF24'] },
  { id: 'ai', label: 'AI analysis', i18nKey: 'searcher_v2.cat.ai', icon: 'brain', gradient: ['#EC4899', '#F472B6'] },
  { id: 'other', label: 'Other', i18nKey: 'searcher_v2.cat.other', icon: 'grid', gradient: ['#64748B', '#94A3B8'] },
]

export const categoryMeta = (id: CategoryId): CategoryMeta =>
  CATEGORIES.find((c) => c.id === id) || CATEGORIES[CATEGORIES.length - 1]

/* ------------------------------------------------------------------ */
/* Source (= sub-type) -> input kind                                   */
/* `lookup.sources[]` IS the sub-type list. combined_id (BreachScan)   */
/* ships vkid / instagramid / password / facebookid / linkedinid /     */
/* telegramid / twitterid / fullname — exactly the 8 sub-types.        */
/* ------------------------------------------------------------------ */

export const SOURCE_INPUT_KIND: Record<string, InputKind> = {
  phone: 'phone',
  geolocation_number: 'phone',

  email: 'email',
  email_domain_validator: 'email',

  fullname: 'name',
  country_fullname: 'name',

  password: 'password',

  vkid: 'social',
  instagramid: 'social',
  facebookid: 'social',
  linkedinid: 'social',
  telegramid: 'social',
  twitterid: 'social',
  glook_id: 'social',
  insta_story: 'social',
  threads: 'social',
  instanavigation_person: 'social',
  summary: 'social',
  psych: 'social',
  common_psychoprofile: 'social',
  fb_group_members: 'social',

  geolocation: 'ip',
  geolocation_proxy: 'ip',

  phone_list: 'bulk',
  email_list: 'bulk',

  kyc_iban: 'text',
  kyc_vat_validate: 'text',
  kyc_company_enrich: 'text',
  web_collection_screenshot: 'text',
  web_collection_scrape: 'text',
}

/** Human label for a sub-type chip. Prefer i18n `searcher.source.<source>`. */
export const SOURCE_LABEL: Record<string, string> = {
  email: 'E-mail',
  phone: 'Phone',
  password: 'Password',
  fullname: 'Full name',
  country_fullname: 'Full name',
  vkid: 'VK ID',
  instagramid: 'Instagram ID',
  facebookid: 'Facebook ID',
  linkedinid: 'LinkedIn ID',
  telegramid: 'Telegram ID',
  twitterid: 'Twitter ID',
  glook_id: 'Username',
  insta_story: 'Instagram story',
  threads: 'Threads',
  instanavigation_person: 'Instagram person',
  geolocation: 'IP geolocation',
  geolocation_proxy: 'IP + proxy check',
  geolocation_number: 'Phone vs Geolocation',
  email_domain_validator: 'E-mail domain',
  kyc_iban: 'IBAN',
  kyc_vat_validate: 'VAT number',
  kyc_company_enrich: 'Company domain',
  web_collection_screenshot: 'Screenshot',
  web_collection_scrape: 'Scrape',
  phone_list: 'Phone list',
  email_list: 'E-mail list',
  summary: 'Summary',
  psych: 'Psychological Portrait',
  common_psychoprofile: 'Common Psychoprofile',
  fb_group_members: 'Facebook group',
}

/** Brand dot colour for social sub-type chips. */
export const SOURCE_COLOR: Record<string, string> = {
  vkid: '#0077FF',
  instagramid: '#E4405F',
  facebookid: '#1877F2',
  linkedinid: '#0A66C2',
  telegramid: '#229ED9',
  twitterid: '#1D9BF0',
  threads: '#000000',
  glook_id: '#64748B',
  reddit: '#FF4500',
  // psycho_profile's three sources all take a Facebook profile URL/ID (see
  // INPUT_PLACEHOLDER's 'FacebookID' hint for them) — same brand mark and
  // colour as facebookid above, not the generic social silhouette.
  summary: '#1877F2',
  psych: '#1877F2',
  common_psychoprofile: '#1877F2',
}

/**
 * Per-source brand mark, as a filled SVG path on a 24-grid — same shapes as
 * NETWORK_ICON in container/irbispro-v2/config/sources.ts (kept as a
 * separate copy here rather than a cross-feature import — see that file's
 * own "ONE TABLE, TWO CONSUMERS" note for why this codebase already accepts
 * that duplication over reaching across feature folders).
 *
 * Used by SearchValueField.tsx's step-3 input so a Telegram ID field shows
 * the Telegram paper-plane and not the same generic silhouette every other
 * *_id source used to fall back to.
 */
export const SOURCE_ICON: Record<string, string> = {
  vkid: 'M13 18.3c-6.6 0-10.4-4.6-10.5-12.2h3.3c.1 5.6 2.6 8 4.5 8.5V6.1h3.1v4.8c1.9-.2 3.9-2.4 4.6-4.8h3.1c-.5 3-2.6 5.2-4.1 6.1 1.5.7 3.9 2.6 4.8 6.1h-3.4c-.7-2.2-2.5-3.9-5-4.2v4.2z',
  instagramid: 'M12 2.2c-2.66 0-3 .01-4.05.06-1.04.05-1.76.22-2.38.46a4.8 4.8 0 0 0-1.74 1.13A4.8 4.8 0 0 0 2.7 5.6c-.24.62-.4 1.33-.45 2.38C2.2 9 2.2 9.35 2.2 12s.01 3 .06 4.05c.05 1.04.21 1.76.45 2.38a4.8 4.8 0 0 0 1.13 1.74 4.8 4.8 0 0 0 1.74 1.13c.62.24 1.34.4 2.38.45 1.05.05 1.39.06 4.05.06s3-.01 4.05-.06c1.04-.05 1.76-.21 2.38-.45a5 5 0 0 0 2.87-2.87c.24-.62.4-1.34.45-2.38.05-1.05.06-1.39.06-4.05s-.01-3-.06-4.05c-.05-1.04-.21-1.76-.45-2.38a4.8 4.8 0 0 0-1.13-1.74 4.8 4.8 0 0 0-1.74-1.13c-.62-.24-1.34-.4-2.38-.46C15 2.21 14.66 2.2 12 2.2m0 1.77c2.6 0 2.94.01 3.97.06.96.04 1.48.2 1.83.33.46.18.79.4 1.13.74.34.34.56.67.74 1.13.13.35.29.87.33 1.83.05 1.03.06 1.34.06 3.94s-.01 2.91-.06 3.94c-.04.96-.2 1.48-.33 1.83a3 3 0 0 1-.74 1.13c-.34.34-.67.56-1.13.74-.35.13-.87.29-1.83.33-1.03.05-1.34.06-3.97.06s-2.94-.01-3.97-.06c-.96-.04-1.48-.2-1.83-.33a3 3 0 0 1-1.13-.74 3 3 0 0 1-.74-1.13c-.13-.35-.29-.87-.33-1.83-.05-1.03-.06-1.34-.06-3.94s.01-2.91.06-3.94c.04-.96.2-1.48.33-1.83.18-.46.4-.79.74-1.13a3 3 0 0 1 1.13-.74c.35-.13.87-.29 1.83-.33C9.06 3.98 9.4 3.97 12 3.97m0 3.02a5.01 5.01 0 1 0 0 10.02 5.01 5.01 0 0 0 0-10.02m0 8.26a3.25 3.25 0 1 1 0-6.5 3.25 3.25 0 0 1 0 6.5m6.38-8.46a1.17 1.17 0 1 1-2.34 0 1.17 1.17 0 0 1 2.34 0',
  facebookid: 'M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5H16.7V3.6a22 22 0 0 0-2.4-.12c-2.38 0-4 1.45-4 4.12v2.3H7.6V13h2.7v8z',
  linkedinid: 'M6.94 8.4H3.6V21h3.34zM5.27 3a1.94 1.94 0 1 0 0 3.87 1.94 1.94 0 0 0 0-3.87M21 13.9c0-3.2-1.7-4.7-3.98-4.7-1.83 0-2.65 1.01-3.11 1.72V8.4H10.6c.04.94 0 12.6 0 12.6h3.31v-7.04c0-.3.02-.6.11-.81.24-.6.79-1.21 1.71-1.21 1.21 0 1.69.92 1.69 2.26V21H21z',
  telegramid: 'M21.9 4.3 18.6 20c-.25 1.1-.9 1.37-1.82.85l-5.03-3.7-2.43 2.33c-.27.27-.5.5-1.01.5l.36-5.12L18 6.36c.4-.36-.09-.56-.63-.2L6.14 13.4l-4.96-1.55c-1.08-.34-1.1-1.08.23-1.6L20.5 2.72c.9-.33 1.68.2 1.4 1.58',
  twitterid: 'M17.53 3h3.02l-6.6 7.55L21.7 21h-6.03l-4.72-6.17L5.53 21H2.5l7.05-8.07L2.3 3h6.18l4.27 5.64zm-1.06 16.2h1.67L7.6 4.72H5.8z',
  threads: 'M16.9 11.4c-.1-.05-.2-.1-.3-.14-.18-3.3-1.98-5.2-5-5.22h-.04c-1.8 0-3.3.77-4.23 2.18l1.66 1.14c.69-1.05 1.78-1.28 2.57-1.28h.03c.98 0 1.72.29 2.2.85.35.4.58.97.7 1.68a12.6 12.6 0 0 0-2.83-.14c-2.85.17-4.68 1.83-4.56 4.14.06 1.17.65 2.18 1.65 2.84.85.55 1.94.82 3.08.76 1.5-.08 2.68-.65 3.5-1.7.62-.79 1.02-1.81 1.19-3.1.72.43 1.25 1 1.55 1.68.5 1.16.53 3.07-1.02 4.62-1.36 1.36-3 1.95-5.47 1.97-2.75-.02-4.83-.9-6.18-2.61C4.13 17.53 3.48 15.2 3.46 12c.02-3.2.67-5.53 1.94-7.06C6.75 3.22 8.83 2.34 11.58 2.32c2.77.02 4.88.9 6.28 2.63.69.85 1.21 1.91 1.55 3.15l1.94-.52c-.42-1.52-1.07-2.83-1.96-3.92C17.6 1.44 14.98.32 11.58.3h-.01C8.19.32 5.6 1.45 3.9 3.66 2.37 5.62 1.59 8.35 1.56 11.99v.02c.03 3.64.81 6.37 2.34 8.33 1.7 2.21 4.29 3.34 7.67 3.36h.01c3.01-.02 5.13-.81 6.88-2.56 2.29-2.28 2.22-5.15 1.47-6.9-.54-1.26-1.57-2.28-2.98-2.94m-5.06 4.9c-1.26.07-2.57-.5-2.64-1.72-.05-.9.64-1.91 2.72-2.03.24-.02.47-.02.7-.02.75 0 1.46.07 2.1.21-.24 2.98-1.64 3.5-2.88 3.56',
  reddit: 'M22 12a2.16 2.16 0 0 0-3.66-1.55 10.6 10.6 0 0 0-5.8-1.85l.99-4.65 3.23.69a1.55 1.55 0 1 0 .17-.93l-3.6-.77a.35.35 0 0 0-.42.27l-1.1 5.19a10.6 10.6 0 0 0-5.87 1.85A2.16 2.16 0 1 0 3.6 14.4a4.3 4.3 0 0 0-.05.65c0 3.3 3.84 5.98 8.58 5.98s8.58-2.68 8.58-5.98c0-.22-.02-.44-.05-.65A2.16 2.16 0 0 0 22 12M7.16 13.55a1.55 1.55 0 1 1 3.1 0 1.55 1.55 0 0 1-3.1 0m8.66 4.1c-1.06 1.06-3.09 1.14-3.68 1.14s-2.63-.08-3.68-1.14a.4.4 0 0 1 .57-.57c.67.67 2.1.9 3.11.9s2.45-.23 3.11-.9a.4.4 0 1 1 .57.57m-.28-2.55a1.55 1.55 0 1 1 0-3.1 1.55 1.55 0 0 1 0 3.1',
  // psycho_profile's sources — same Facebook mark as facebookid, see
  // SOURCE_COLOR above for why.
  summary: 'M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5H16.7V3.6a22 22 0 0 0-2.4-.12c-2.38 0-4 1.45-4 4.12v2.3H7.6V13h2.7v8z',
  psych: 'M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5H16.7V3.6a22 22 0 0 0-2.4-.12c-2.38 0-4 1.45-4 4.12v2.3H7.6V13h2.7v8z',
  common_psychoprofile: 'M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5H16.7V3.6a22 22 0 0 0-2.4-.12c-2.38 0-4 1.45-4 4.12v2.3H7.6V13h2.7v8z',
  // Exact-key entries for the two most likely spellings; sourceBrandIcon()
  // below also regex-matches "tiktok"/"snapchat" anywhere in the source
  // string, so a backend variant like `tiktok_id` or `irbis_tiktok` still
  // resolves even if it isn't listed here verbatim.
  tiktok: 'M16.6 2h-3.3v13.4a2.7 2.7 0 1 1-2.7-2.7c.24 0 .47.03.69.09V9.4a6.2 6.2 0 0 0-.69-.04 6.1 6.1 0 1 0 6.1 6.1V8.9a7.4 7.4 0 0 0 4.2 1.32V6.9a4.1 4.1 0 0 1-4.3-4.9',
  // Simplified ghost silhouette (not the literal brand mark) — enough to
  // read as "not the generic Instagram-shaped glyph" at 19px.
  snapchat: 'M12 2c-3.5 0-6 2.6-6 6.2v2.6c-.9.4-1.8.9-2.6 1.5-.5.4-.4 1.1.2 1.3 1 .3 1.8.5 2.4.7.1.5.3 1.1.6 1.6-.3.4-.7.8-1.1 1.1-.5.4-.3 1.2.3 1.3 1 .2 2 .1 2.7-.1.7 1 1.9 1.6 3.5 1.6s2.8-.6 3.5-1.6c.7.2 1.7.3 2.7.1.6-.1.8-.9.3-1.3-.4-.3-.8-.7-1.1-1.1.3-.5.5-1.1.6-1.6.6-.2 1.4-.4 2.4-.7.6-.2.7-.9.2-1.3-.8-.6-1.7-1.1-2.6-1.5V8.2C18 4.6 15.5 2 12 2z',
}

/**
 * Same TikTok/Snapchat colours as SOURCE_COLOR's other pairs — kept next to
 * sourceBrandIcon so the two never drift out of sync.
 */
const REGEX_BRAND_ICON: { re: RegExp; path: string; color: string }[] = [
  { re: /tiktok/i, path: SOURCE_ICON.tiktok, color: '#000000' },
  { re: /snapchat/i, path: SOURCE_ICON.snapchat, color: '#CC9A00' },
]

/**
 * Resolves a source's brand icon the way SearchValueField.tsx's step-3
 * input needs it: exact SOURCE_ICON key first, then a name-contains regex
 * fallback for TikTok/Snapchat — whose backend `source` strings vary
 * (`tiktok`, `tiktok_id`, `irbis_tiktok`, `snapchat`, `snapchat_id`, ...)
 * and would otherwise silently miss the exact-key map and fall through to
 * the generic social silhouette every other unmatched *_id source uses.
 */
export const sourceBrandIcon = (subtype?: Source | string): { path: string; color: string } | undefined => {
  const raw = String(subtype ?? '')
  if (!raw) return undefined
  if (SOURCE_ICON[raw]) return { path: SOURCE_ICON[raw], color: SOURCE_COLOR[raw] || '#888' }
  const hit = REGEX_BRAND_ICON.find((r) => r.re.test(raw))
  return hit ? { path: hit.path, color: hit.color } : undefined
}

export const sourceLabel = (s: Source | string): string => {
  const raw = String(s ?? '')
  if (SOURCE_LABEL[raw]) return SOURCE_LABEL[raw]
  // Title-case an unknown source id so custom workflows still read well,
  // e.g. "sniper api" -> "Sniper API", "geolocation_number" -> "Geolocation number"
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\b(api|id|ip|vk|fb|kyc|vat|iban)\b/gi, (m) => m.toUpperCase())
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
}

/**
 * Which input kind a sub-type expects.
 *
 * Order: explicit map -> name heuristics -> the workflow's own fallback.
 *
 * The fallback matters. Custom workflows ship sub-types that are not in
 * SOURCE_INPUT_KIND — "sniper api", a list of 33 country names on the profiler,
 * anything the admin adds tomorrow. Without a fallback every one of those
 * collapsed into "Text / domain", which merged unrelated workflows into a
 * single input tile and gave a face search a plain text field.
 */
export const sourceInputKind = (s: Source | string, fallback: InputKind = 'text'): InputKind => {
  const key = String(s ?? '').toLowerCase().trim()
  if (!key) return fallback
  if (SOURCE_INPUT_KIND[key]) return SOURCE_INPUT_KIND[key]

  if (/(^|[\s_-])(phone|msisdn|tel|mobile|number)([\s_-]|$)/.test(key)) return 'phone'
  if (/mail/.test(key)) return 'email'
  if (/(face|photo|image|picture|sniper|selfie|scene)/.test(key)) return 'image'
  if (/(^|[\s_-])(ip|geoloc\w*|geolocation)([\s_-]|$)/.test(key)) return 'ip'
  if (/(pass(word)?|hash|credential)/.test(key)) return 'password'
  if (/(fullname|full_name|name)/.test(key)) return 'name'
  if (/(list|bulk|csv|batch)/.test(key)) return 'bulk'
  if (/(id$|_id|handle|username|nick|insta|vk|facebook|telegram|twitter|linkedin|threads|tiktok|bluesky)/.test(key))
    return 'social'

  return fallback
}

/* ------------------------------------------------------------------ */
/* Type -> display name (the "I have" carousel tiles)                   */
/* ------------------------------------------------------------------ */

export const TYPE_NAMES: Record<string, string> = {
  phone: 'Phone',
  name: 'Name Search',
  name_api: 'Name API',
  post: 'Post Search',
  posts_by_keywords: 'Keyword Posts',
  deepweb: 'Deep Web',
  webint: 'WebInt',
  combined_phone: 'Phone Lookup',
  phone_list: 'Phone List',
  combined_email: 'Email Lookup',
  email_list: 'Email List',
  combined_name: 'Name Lookup',
  name_by_country: 'Name by Country',
  combined_id: 'ID Lookup',
  face_search: 'Face Search',
  additional_quotas: 'Add Quotas',
  web_data_search: 'Facebook Crawler',
  irbis_facebook: 'Facebook',
  irbis_instagram: 'Instagram',
  crolo_x: 'X',
  crolo_vk: 'VK',
  crolo_ok: 'OK',
  irbis_tiktok: 'TikTok',
  bluesky_v3: 'BlueSky',
  fb_person_wall_keyword: 'Facebook Wall',
  sentiment_analysys: 'Sentiment',
  irbis_eye: 'Irbis Eye',
  image_insize: 'Photo Intel',
  photo_searcher: 'Sniper',
  kyc: 'KYC',
  web_collection: 'Web Collection',
  ip_geo: 'IP Geolocation',
  ipgeo_number: 'Phone Geolocation',
  psycho_profile: 'PsychoProfile',
  api_v2_x: 'X API v2',
  api_v2_instagram: 'Instagram API v2',
  api_v2_linkedin: 'LinkedIn API v2',
  lookup_phone_verification: 'Phone Validation',
  lookup_phone_name_verification: 'Phone/Name Validation',
  real_phone: 'Real Phone',
  irbis_criminal: 'Criminal Check',
  ssn_trace: 'SSN Trace',
  nat_crim_check: 'Criminal Screening',
  pep: 'PEP Check',
  sanctions_person_company: 'Sanctions Search',
  sanctions_pep: 'Sanctions PEP',
  watchlists: 'Watchlists',
  court_records: 'Court Records',
  adid: 'AdID by Location',
  adid_by_phone: 'AdID by Phone',
  adid_location: 'AdID Location',
  profiler: 'Profiler',
  group_members: 'Group Members',
  psycho_profile_summary: 'OSINT PsychoProfile',
  sn_api_profiler: 'Social Profiler',
  sn_api_profiler_fb_friends: 'Facebook Friends',
  sn_api_profiler_fb_posts: 'Facebook Posts',
  sn_api_profiler_insta_friends: 'Instagram Friends',
  sn_api_profiler_insta_posts: 'Instagram Posts',
  sn_api_group_profiler: 'Group Profiler',
  posts_comments: 'Posts & Comments',
}

/** Display name for a raw lookup `type` — falls back to a title-cased id for anything not in TYPE_NAMES (new/custom workflow types). */
export const typeName = (type: Type | string): string => {
  const raw = String(type ?? '')
  if (TYPE_NAMES[raw]) return TYPE_NAMES[raw]
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\b(api|id|ip|vk|fb|kyc|ssn|pep|adid)\b/gi, (m) => m.toUpperCase())
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
}

/* ------------------------------------------------------------------ */
/* Type -> category, fallback input kind, description, returns          */
/* ------------------------------------------------------------------ */

type TypeMeta = {
  category: CategoryId
  /** used only when the lookup has no `sources[]` */
  inputKind: InputKind
  description?: string
  returns?: string[]
  /** approximate runtime shown in the rail, e.g. '8–14s' */
  eta?: string
  isNew?: boolean
}

export const TYPE_META: Partial<Record<Type | string, TypeMeta>> = {
  phone: {
    category: 'phone', inputKind: 'phone', eta: '8–14s',
    description: 'Identity behind a number across messenger and caller-ID sources.',
    returns: ['Names and aliases', 'Profile photos', 'Linked messenger accounts', 'Birthday, if published', 'Carrier, line type and country'],
  },
  combined_phone: {
    category: 'phone', inputKind: 'phone', eta: '8–14s',
    description: 'Combined phone lookup across every connected provider.',
    returns: ['Names and aliases', 'Profile photos', 'Linked messenger accounts', 'E-mail addresses', 'Carrier and country'],
  },
  real_phone: {
    category: 'phone', inputKind: 'phone', eta: '2–5s',
    description: 'Line validity and carrier check.',
    returns: ['Line status', 'Carrier', 'Line type'],
  },
  ipgeo_number: {
    category: 'phone', inputKind: 'phone', eta: '1–3s',
    description: 'Cross-check a phone country against an IP or validate an e-mail domain.',
    returns: ['Match or mismatch verdict', 'Phone country vs IP country', 'Risk flag'],
  },
  phone_list: {
    category: 'phone', inputKind: 'bulk', eta: 'per row',
    description: 'Phone lookup over a list, billed per row.',
    returns: ['One record per row', 'Downloadable XLSX', 'Per-row status and error log'],
  },

  combined_email: {
    category: 'email', inputKind: 'email', eta: '8–14s',
    description: 'Accounts, profiles and identity registered to an address.',
    returns: ['Registered account list', 'Display names', 'Profile photos', 'Linked phone numbers'],
  },
  email_list: {
    category: 'email', inputKind: 'bulk', eta: 'per row',
    description: 'E-mail lookup over a list, billed per row.',
    returns: ['One record per row', 'Downloadable XLSX', 'Per-row status and error log'],
  },
  // Real catalogue titles for this type are "Instagram ID", "Instagram ID
  // WebScan" and "Reddit Lookup" — a social-platform ID lookup, not a breach
  // search (that's `deepweb`, below). Category moved from 'email' to
  // 'digital' to match: it has nothing to do with e-mail/leaks shelving.
  combined_id: {
    category: 'digital', inputKind: 'social', eta: '4–8s',
    description: 'Look up a social platform ID (Instagram, Reddit and others) across connected sources.',
    returns: ['Linked profile handle and URL', 'Display name and bio where public', 'Activity and account signals', 'Cross-platform matches'],
  },
  // Real catalogue title is "BreachScan" (subTitle: "Compromised Social
  // Network ID"). Its actual `sources` are telegramid/vkid/instagramid/
  // password/facebookid/linkedinid/twitterid/fullname/email/phone — 6 of
  // the 10 are social-platform IDs, email is just one option among many —
  // so inputKind is 'social', not 'email' (that was wrongly pulling it onto
  // the E-mail tile alongside combined_email, inflating it to 3 cards).
  // Category stays 'email' — that shelf is "E-mail & leaks" by theme, not
  // by input type, and this genuinely is a leak/breach scanner.
  deepweb: {
    category: 'email', inputKind: 'social', eta: '15–30s',
    description: 'Leaked-database search by social ID, e-mail, phone or password across breach dumps and pastes.',
    returns: ['Breach names and dates', 'Records and pastes containing the identifier', 'Source URLs', 'First and last seen dates'],
  },

  name: {
    category: 'name', inputKind: 'name', eta: '6–10s',
    description: 'Match a full name to social profiles.',
    returns: ['Candidate profiles per platform', 'Match confidence', 'Avatars and profile URLs'],
  },
  combined_name: {
    category: 'name', inputKind: 'name', eta: '10–20s',
    description: 'Open-web mentions, articles and public records for a person.',
    returns: ['Ranked web mentions', 'Snippets', 'Source URLs and dates'],
  },
  name_api: { category: 'name', inputKind: 'name', eta: '6–12s', description: 'Name lookup via the partner API.' },
  name_by_country: {
    category: 'name', inputKind: 'name', eta: '10–20s',
    description: 'Name search scoped to a single country.',
    returns: ['Country-scoped matches', 'Public records', 'Source URLs'],
  },
  country_fullname: { category: 'name', inputKind: 'name', eta: '10–20s', description: 'Full-name search by country.' },
  face_search: {
    category: 'name', inputKind: 'image', eta: '10–20s',
    description: 'Reverse face search across social platforms.',
    returns: ['Similar faces with match percentage', 'Platform and profile link', 'Thumbnail of each match'],
  },
  photo_searcher: { category: 'name', inputKind: 'image', eta: '10–20s', description: 'Photo-based profile search.' },
  irbis_eye: {
    category: 'name', inputKind: 'image', eta: '12–25s', isNew: true,
    description: 'Analyse a photo scene — objects, location cues and context.',
    returns: ['Detected objects and text', 'Location and setting cues', 'Scene description'],
  },
  image_insize: { category: 'name', inputKind: 'image', eta: '10–20s', description: 'Image-based identification.' },

  ip_geo: {
    category: 'digital', inputKind: 'ip', eta: '2–4s',
    description: 'Location, ISP, ASN and risk signals behind an IP address.',
    returns: ['City, region and country', 'ISP and ASN', 'Proxy / VPN / hosting flag', 'Coordinates and accuracy radius'],
  },
  web_collection: { category: 'digital', inputKind: 'text', eta: '10–30s', description: 'Collect and archive a public web page.' },
  kyc: { category: 'digital', inputKind: 'text', eta: '4–10s', description: 'Company, VAT and IBAN verification.' },
  group_members: { category: 'digital', inputKind: 'social', eta: '20–60s', description: 'Enumerate the members of a public group.' },
  // Real catalogue title is "Leads Search".
  post: { category: 'digital', inputKind: 'text', eta: '10–20s', description: 'Search public posts by keyword to surface leads.' },
  // Was in HIDDEN_TYPES (silently filtered out of the catalogue) despite
  // being a real, titled workflow ("Posts By Keywords") — un-hidden below
  // and given an explicit entry instead of falling through to the generic
  // inferTypeMeta() guess.
  posts_by_keywords: { category: 'digital', inputKind: 'text', eta: '10–20s', description: 'Search public posts by keyword.' },

  psycho_profile: {
    category: 'ai', inputKind: 'social', eta: '20–40s', isNew: true,
    description: 'Behavioural profile built from public digital traces.',
    returns: ['Personality trait estimates', 'Interests and topics', 'Activity patterns', 'Confidence and source basis'],
  },
  sentiment_analysys: {
    // Old UI's LookupInline.tsx opens PopupTextUpload.tsx for this type — a
    // free-text paragraph (a post/comment to analyse), not an identifier —
    // and the backend agrees (Joi.string().min(1).max(3500) in
    // searchSchema.ts). 'longtext' is the dedicated multi-line kind for it;
    // it used to be 'text', which rendered a single-line field.
    category: 'ai', inputKind: 'longtext', eta: '15–30s',
    description: 'Tone and sentiment across public content.',
    returns: ['Overall sentiment score', 'Sentiment over time', 'Topic breakdown'],
  },
  profiler: { category: 'ai', inputKind: 'phone', eta: '30–60s', description: 'Full profiler report.' },
  // DeveloperCryptoWalletScreeningLookupDto — no TYPE_META entry existed for
  // public_company_info either (it falls through to inferTypeMeta), but this
  // one gets an explicit entry since "wallet"/"crypto" don't match any of
  // inferTypeMeta's keyword regexes and would otherwise land on the generic
  // Other/Text guess.
  crypto_wallet_screening: {
    category: 'other', inputKind: 'text', eta: '5–15s',
    description: 'AML risk and exposure screening for a blockchain wallet address.',
    returns: ['Risk score and flags', 'Exposure to sanctioned or illicit entities', 'Transaction history summary', 'Network and address details'],
  },
  // PublicCompanyInfoLookupDto — 5 optional fields (companyName, companyId,
  // website, jurisdiction, language), no single "main value" (saga.ts posts
  // `additionalParameters` only, see PublicCompanyInfoFieldCards.tsx). Lives
  // on its own 'kyc' case-2 shelf (TYPE_GROUPS below) rather than the
  // existing 'company' group, per product decision.
  public_company_info: {
    category: 'other', inputKind: 'text', eta: '5–15s',
    description: 'Registry lookup for a public company — name, ID, website or jurisdiction.',
    returns: ['Registered company name and status', 'Registration/company ID', 'Jurisdiction and incorporation details', 'Officers and filings, where available'],
  },
  // DeveloperRegionMonitoringLookupDto — no keyword in inferTypeMeta's regex
  // list matches "region monitoring", so it needs an explicit entry the same
  // way crypto_wallet_screening did above.
  region_monitoring: {
    category: 'other', inputKind: 'text', eta: '15–45s',
    description: 'Ongoing monitoring feed for a region — recent items over a chosen time window.',
    returns: ['Matching items for the region and period', 'Item timestamps and source links', 'A result ID for entity extraction'],
  },
  // DeveloperRegionMonitoringEntitiesExtractLookupDto — a follow-up call that
  // extracts named entities from a region_monitoring result already run
  // (its `resultId`), not a fresh search of its own.
  region_monitoring_entities_extract: {
    category: 'other', inputKind: 'text', eta: '5–15s',
    description: 'Named-entity extraction from a previously run region monitoring result.',
    returns: ['People, organisations and locations mentioned', 'Counts per entity', 'Source items each entity was found in'],
  },
}

/**
 * Guess category and input kind for a workflow this file has never seen.
 *
 * Custom and white-label workflows ("MCP Face Recognition", "Reports by USA",
 * country profilers) are added in admin all the time. Without this they all
 * landed in Other / Text, which is what made the console look broken on
 * environments with a small custom catalogue.
 */
function inferTypeMeta(type: string, title = ''): TypeMeta {
  const s = `${type} ${title}`.toLowerCase()

  if (/(face|photo|image|picture|selfie|eye|scene|recognition)/.test(s))
    return { category: 'name', inputKind: 'image' }
  if (/(psych|sentiment|portrait|\bai\b|gpt|llm)/.test(s))
    return { category: 'ai', inputKind: 'social' }
  if (/(breach|leak|pluto|hydra|password|combo)/.test(s))
    return { category: 'email', inputKind: 'email' }
  if (/(bulk|list|csv|batch)/.test(s))
    return { category: 'other', inputKind: 'bulk' }
  if (/mail/.test(s)) return { category: 'email', inputKind: 'email' }
  if (/(phone|msisdn|caller|number)/.test(s)) return { category: 'phone', inputKind: 'phone' }
  if (/(ip\b|geoloc|geo\b)/.test(s)) return { category: 'digital', inputKind: 'ip' }
  if (/(instagram|facebook|vk|telegram|twitter|linkedin|tiktok|social|profile|webscan)/.test(s))
    return { category: 'digital', inputKind: 'social' }
  if (/(name|person|profiler|report|kyc|record|court|sanction)/.test(s))
    return { category: 'name', inputKind: 'name' }

  return { category: 'other', inputKind: 'text' }
}

/**
 * @param type  ILookup.type
 * @param title ILookup.title — used only when `type` is unknown, so a custom
 *              workflow still lands on a sensible shelf.
 */
export const typeMeta = (type: Type | string, title?: string): TypeMeta =>
  TYPE_META[type as Type] || inferTypeMeta(String(type ?? ''), title)

/**
 * Category override for a lookup ROW based on its actual source, not its
 * type. TYPE_META only holds one category per type, but `ipgeo_number` is
 * two genuinely different lookups sharing one type: "Phone Vs IP Validator"
 * (source `geolocation_number`, phone) and "E-mail Validator" (source
 * `email_domain_validator`, e-mail) — see TYPE_GROUPS' `sourcesByType` doc
 * comment, which already special-cases this same split for the case-2
 * grouped shelf. Without this, the E-mail Validator row inherited
 * ipgeo_number's default 'phone' category everywhere else category grouping
 * is used (Workflow drawer/palette, category-coloured badges, ...).
 */
const CATEGORY_OVERRIDE_BY_SOURCE: Partial<Record<string, CategoryId>> = {
  email_domain_validator: 'email',
}

export const categoryForItem = (subtypes: (Source | string)[], fallback: CategoryId): CategoryId => {
  for (const s of subtypes) {
    const override = CATEGORY_OVERRIDE_BY_SOURCE[String(s)]
    if (override) return override
  }
  return fallback
}

/** Generic "You will get" copy for a workflow with no declared returns[]. */
export const fallbackReturns = (category: CategoryId): string[] => {
  switch (category) {
    case 'phone':
      return ['Whatever the connected phone providers return', 'Names and photos where published', 'Carrier and country details']
    case 'email':
      return ['Accounts registered to the address', 'Linked profiles and display names', 'Breach exposure where available']
    case 'name':
      return ['Candidate profiles and records', 'Photos where published', 'Source links for every match']
    case 'digital':
      return ['Profile or network details', 'Linked identifiers', 'Source links']
    case 'ai':
      return ['A generated narrative summary', 'Model confidence where provided', 'The evidence the model used']
    default:
      return ['Everything the provider returns for this input']
  }
}

/**
 * Types hidden from the Search UI. Lifted verbatim out of the old
 * SearcherPage `.filter(...)` chain so it lives in one place. Ideally the
 * backend flags these with `visible: false` (BACKEND_CONTRACT.md §2.1) and
 * this array becomes empty.
 *
 * Independent from HIDDEN_FROM_DEVELOPER_API_TYPES below — Search and
 * API-Developer used to share one blocklist (HIDDEN_TYPES), which meant a
 * type hidden from manual Search was also unreachable via the public API
 * even when it had a real, callable endpoint (e.g. `public_company_info`).
 * Split so each catalogue states its own list; a type left out of BOTH is
 * visible everywhere, and a type can now be API-only or Search-only.
 */
export const HIDDEN_FROM_SEARCH_TYPES: string[] = [
  'webint', 'web_data_search', 'fb_person_wall_keyword', 'api_v2_instagram',
  'api_v2_linkedin', 'api_v2_x', 'sn_api_profiler',
  'lookup_phone_verification',
  'sn_api_profiler_fb_friends', 'sn_api_profiler_fb_posts',
  'sn_api_profiler_insta_friends', 'sn_api_profiler_insta_posts',
  'irbis_facebook', 'irbis_instagram', 'crolo_x', 'crolo_vk', 'crolo_ok',
  'bluesky_v3', 'irbis_tiktok', 'irbis_criminal', 'sn_api_group_profiler',
  'ssn_trace', 'nat_crim_check', 'sanctions_person_company', 'sanctions_pep',
  'watchlists', 'pep', 'court_records', 'adid', 'adid_by_phone',
  'adid_location', 'posts_comments',
  'psycho_profile_summary',
  // region_monitoring_entities_extract is never a standalone search — it's a
  // one-time "Extract entities" follow-up action tied to an EXISTING
  // region_monitoring result (RegionMonitoringEntitiesExtractLookupDto: just
  // lookupId + resultId, no `key`, no value of its own), triggered only from
  // the "Continue the investigation" button in ResultView.tsx. It has no
  // TYPE_GROUPS entry either, so with the console now always rendering the
  // grouped view this is already unreachable — listed here too as an
  // explicit safety net in case that toggle is ever restored.
  'region_monitoring_entities_extract',
  // 'real_phone' and 'lookup_phone_name_verification' are intentionally NOT
  // in this list: they're visible on the Search page but locked, not
  // runnable — see API_ONLY_TYPES below.
]

/**
 * Visible on the Search page, but not runnable there — the backend only
 * exposes these through the public Developer API (see
 * developer-v2/config/endpoints.ts), unlike everything in
 * HIDDEN_FROM_SEARCH_TYPES above which doesn't even appear. Their step-2
 * "Search method" card renders locked with an "API only" ribbon instead of
 * being dropped from the catalogue entirely — see WorkflowCards.tsx.
 */
export const API_ONLY_TYPES: string[] = ['real_phone', 'lookup_phone_name_verification']

export const isApiOnlyType = (type: string): boolean => API_ONLY_TYPES.includes(type)

/**
 * Types hidden from the API-Developer explorer. `email_list` and
 * `phone_list` are bulk CSV lookups — portal-only, confirmed by the product
 * owner to have no public API (see endpoints.ts's file header) — so they
 * must stay off the endpoint list even if a path is ever added to
 * LOOKUP_PATH_MAP by mistake. Independent from HIDDEN_FROM_SEARCH_TYPES
 * above — see that array's comment for why the two are no longer merged.
 */
export const HIDDEN_FROM_DEVELOPER_API_TYPES: string[] = ['email_list', 'phone_list']

/**
 * Types whose `sources[]` are genuinely different search methods (different
 * identifier formats the user must choose between — e.g. deepweb accepts a
 * phone OR an e-mail OR a VK ID, each needing a different field), as opposed
 * to every other type where `sources[]` just lists backend providers queried
 * together behind the scenes (e.g. combined_phone's 20 messenger apps are
 * not a user choice).
 *
 * Deliberately a hardcoded list, not a computed heuristic — the two cases
 * can't be told apart reliably from the source names alone (ip_geo's two
 * sources are both `kind: 'ip'` yet are still a real method choice: plain
 * geolocation vs proxy-aware geolocation).
 *
 * Step 2 ("Search method") expands these types into one card per distinct
 * source instead of one card per workflow row — see
 * hooks/useLookupCatalog.ts `methodsForType`.
 */
export const SOURCE_EXPAND_TYPES: string[] = [
  'deepweb', 'ip_geo', 'psycho_profile', 'combined_id', 'ipgeo_number', 'kyc', 'web_collection',
]

/**
 * Grouped "I have" shelf — case 2 of the step 1 tile row (case 1 stays
 * InputTypeTiles/`inputTypes` above, untouched). Instead of one tile per raw
 * `type` (~59 of them, arrow-scrolled), groups every type by what the user
 * actually holds — a phone number, an email, a photo, and so on — down to 8
 * tiles. Purely additive/opt-in: SearchConsole's case-1/case-2 toggle decides
 * which shelf renders; nothing here changes what case 1 shows.
 *
 * A type left out of every group here still works fine in case 1 — it just
 * won't have a case-2 home yet — and HIDDEN_FROM_SEARCH_TYPES still applies
 * on top (useLookupCatalog only ever sees visible types), so this list can
 * safely include types that are currently hidden from Search without
 * resurrecting them.
 */
export type GroupId = 'phone' | 'email' | 'name' | 'compliance' | 'image' | 'social' | 'kyc' | 'ai'

export type TypeGroupMeta = {
  id: GroupId
  label: string
  i18nKey: string
  icon: string
  types: string[]
  /**
   * Restricts a SOURCE_EXPAND_TYPES member (config below) to specific
   * sources within THIS group only — e.g. `ipgeo_number` genuinely belongs
   * to two different shelves depending on which method the user picks: its
   * `geolocation_number` source (phone vs IP) reads as a Phone workflow,
   * its `email_domain_validator` source (e-mail domain check) reads as an
   * E-mail one. The type is listed in both groups' `types`, and this map
   * says which of its sources each group actually pulls in — see
   * useLookupCatalog.ts `methodsForGroup`.
   */
  sourcesByType?: Partial<Record<string, string[]>>
}

export const TYPE_GROUPS: TypeGroupMeta[] = [
  {
    id: 'phone', label: 'Phone', i18nKey: 'searcher_v2.group.phone', icon: 'phone',
    types: ['phone', 'combined_phone', 'ipgeo_number', 'lookup_phone_name_verification', 'real_phone', 'phone_list'],
    sourcesByType: { ipgeo_number: ['geolocation_number'] },
  },
  {
    id: 'email', label: 'E-mail', i18nKey: 'searcher_v2.group.email', icon: 'mail',
    types: ['combined_email', 'email_list', 'ipgeo_number'],
    sourcesByType: { ipgeo_number: ['email_domain_validator'] },
  },
  {
    id: 'name', label: 'Name', i18nKey: 'searcher_v2.group.name', icon: 'user',
    types: ['combined_name', 'name'],
  },
  {
    id: 'compliance', label: 'Compliance Screening', i18nKey: 'searcher_v2.group.compliance', icon: 'shield',
    types: ['ssn_trace', 'nat_crim_check', 'pep', 'court_records'],
  },
  {
    id: 'image', label: 'Image', i18nKey: 'searcher_v2.group.image', icon: 'image',
    types: ['face_search', 'image_insize'],
  },
  {
    id: 'social', label: 'Social', i18nKey: 'searcher_v2.group.social', icon: 'social',
    types: ['deepweb', 'combined_id', 'psycho_profile_summary', 'region_monitoring'],
  },
  {
    // 'company' ("Company Intelligence") used to hold both of these — now
    // empty and removed, since crypto_wallet_screening (below) was its last
    // member. Both DTOs are compliance-flavoured lookups anyway (screening/
    // registry checks), so one KYC shelf reads better than a near-empty
    // second one next to it.
    id: 'kyc', label: 'KYC', i18nKey: 'searcher_v2.group.kyc', icon: 'shield',
    types: ['public_company_info', 'crypto_wallet_screening'],
  },
  {
    id: 'ai', label: 'AI', i18nKey: 'searcher_v2.group.ai', icon: 'brain',
    types: ['sentiment_analysys', 'psycho_profile'],
  },
]

/**
 * Gradient badge colour for each case-2 "I have" tile (GroupTiles.tsx) —
 * same gradient-badge treatment InputTypeTiles.tsx (case 1) and the old
 * Workflow Library shelves used. phone/email/name/ai reuse CATEGORIES'
 * gradients directly (each group maps onto exactly one category); the other
 * four groups don't have a 1:1 category (image/social both fall under
 * 'digital' or 'name', company/compliance under 'other') so they get their
 * own distinct colour instead of collapsing onto a shared, less specific one.
 */
export const GROUP_GRADIENT: Record<GroupId, [string, string]> = {
  phone: categoryMeta('phone').gradient,
  email: categoryMeta('email').gradient,
  name: categoryMeta('name').gradient,
  ai: categoryMeta('ai').gradient,
  image: ['#06B6D4', '#67E8F9'],
  social: ['#F59E0B', '#FBBF24'],
  compliance: ['#EF4444', '#F87171'],
  // Distinct from 'compliance' (red, court/sanctions-flavoured), from
  // 'name' (teal/green — the original pick here, too close to it in
  // practice), and from 'ai' (soft pink) — fuchsia is saturated enough and
  // leans purple, so it doesn't collide with either.
  kyc: ['#A21CAF', '#E879F9'],
}

export const groupGradientMeta = (id: GroupId): [string, string] => GROUP_GRADIENT[id] || CATEGORIES[CATEGORIES.length - 1].gradient

/**
 * Which case-2 group a raw `type` belongs to, if any. `source` disambiguates
 * types that are split across groups by sub-type (see `sourcesByType` above,
 * currently only `ipgeo_number`) — without it, or when the source isn't
 * restricted in any matching group, the first group listing the type wins.
 */
export const groupForType = (type: string, source?: string): GroupId | undefined => {
  const candidates = TYPE_GROUPS.filter((g) => g.types.includes(type))
  if (candidates.length <= 1) return candidates[0]?.id
  if (source) {
    const bySource = candidates.find((g) => g.sourcesByType?.[type]?.includes(source))
    if (bySource) return bySource.id
  }
  return candidates[0]?.id
}

/** Placeholder per sub-type. Kept identical to LookupInline.placeholderMap. */
export const PLACEHOLDER_BY_SOURCE: Record<string, string> = {
  email: 'Enter email',
  facebookid: 'Facebook ID',
  fullname: 'Name Surname',
  linkedinid: 'LinkedIn ID',
  password: 'password',
  phone: '+123456789',
  telegramid: 'Telegram ID',
  vkid: 'Vk ID (vkontakte)',
  twitterid: 'Twitter ID',
  instagramid: 'Instagram ID',
  instanavigation_person: 'Instagram ID:',
  glook_id: 'username',
  insta_story: 'username',
  threads: 'username',
  geolocation: '138.162.214.215',
  geolocation_proxy: '138.162.214.215',
  geolocation_number: '+123456789',
  country_fullname: 'Full Name',
  phone_list: '+123456789; +123456789; ... +123456789;',
  email_list: 'email1@gmail.com; email2@hotmail.com;',
  summary: 'FacebookID',
  psych: 'FacebookID',
  common_psychoprofile: 'FacebookID',
  fb_group_members: 'Facebook GroupID',
  // Were missing entirely — SOURCE_EXPAND_TYPES now expands kyc into one
  // card per source (see below), each with its own field, so each needs
  // its own hint rather than falling through to the generic 'text' example.
  kyc_iban: 'DE89370400440532013000',
  kyc_vat_validate: 'DE123456789',
  kyc_company_enrich: 'example.com',
  email_domain_validator: 'email@gmail.com',
  // Matches old UI's field.hint.text ("Enter text here" — PopupTextUpload.tsx,
  // opened from LookupInline.tsx's sentiment_analysys case). It's a free-text
  // passage to analyse, not an identifier — see TYPE_META.sentiment_analysys.
  sentiment_analysys: 'Enter text here',
}

/**
 * Default hint text shown inside an input before the user types anything —
 * step 3's placeholder. Centralises what used to be resolved inline in
 * SearchValueField.tsx so every field (including the profiler-name and
 * name-by-country fields, which don't come from a lookup `source`) gets a
 * consistent placeholder through one place.
 *
 * Order: an explicit per-source placeholder (PLACEHOLDER_BY_SOURCE) -> a
 * caller-supplied fallback (e.g. the workflow's own raw `input` example
 * from the backend, or a field-specific default the caller passes in) ->
 * a generic example for the input kind (INPUT_KINDS[...].example) -> ''.
 */
export const fieldHint = (kind: InputKind, subtype?: Source | string, fallback?: string): string => {
  const bySource = subtype ? PLACEHOLDER_BY_SOURCE[String(subtype)] : undefined
  return bySource || fallback || inputKindMeta(kind).example || ''
}
